import { useEffect, useRef, useState } from 'react';

/**
 * Rolls a number up to `target` once, over `durationMs`. Cheap delight on money
 * heroes — animates only when the value actually changes, and never for users
 * who asked for reduced motion (returns the final value immediately).
 *
 * Re-fires when `target` changes, easing from the previously-shown value so a
 * refresh that nudges the total counts the delta, not the whole amount.
 */
export function useCountUp(target: number, durationMs = 320): number {
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const [value, setValue] = useState(prefersReduced ? target : 0);
  const fromRef = useRef(prefersReduced ? target : 0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReduced || !Number.isFinite(target)) {
      setValue(target);
      fromRef.current = target;
      return;
    }
    const from = fromRef.current;
    const delta = target - from;
    if (delta === 0) return;

    const step = (now: number) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + delta * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
        startRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
      fromRef.current = target;
    };
  }, [target, durationMs, prefersReduced]);

  return value;
}
